import type { ShortcutSuggestionData } from './const';
import { getShortcutSuggestionDetail } from '@services/shortcut';
import { createWegicTag, WEGIC_TAGS } from '@/components/Markdown/MarkdownViewer/CustomTagCom';
import {
  SHORTCUT_SUGGESTIONS_STORAGE_KEY,
  SHORTCUT_SUGGESTIONS_STORAGE_MAX_PROJECTS,
} from './const';

type ShortcutSuggestionsStorage = Record<string, ShortcutSuggestionData>;

function isValidShortcutSuggestionData(data: unknown): data is ShortcutSuggestionData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const suggestionData = data as Partial<ShortcutSuggestionData>;
  return Boolean(
    suggestionData.projectId &&
      typeof suggestionData.projectId === 'string' &&
      Array.isArray(suggestionData.suggestions) &&
      typeof suggestionData.timestamp === 'number',
  );
}

function readShortcutSuggestionsStorage(): ShortcutSuggestionsStorage {
  const data = localStorage.getItem(SHORTCUT_SUGGESTIONS_STORAGE_KEY);
  if (!data) {
    return {};
  }

  try {
    const parsedData = JSON.parse(data) as Record<string, unknown>;
    if (!parsedData || typeof parsedData !== 'object' || Array.isArray(parsedData)) {
      localStorage.removeItem(SHORTCUT_SUGGESTIONS_STORAGE_KEY);
      return {};
    }

    const storage: ShortcutSuggestionsStorage = {};
    Object.entries(parsedData).forEach(([projectId, value]) => {
      if (isValidShortcutSuggestionData(value)) {
        storage[projectId] = value;
      }
    });

    return storage;
  } catch {
    localStorage.removeItem(SHORTCUT_SUGGESTIONS_STORAGE_KEY);
    return {};
  }
}

function writeShortcutSuggestionsStorage(storage: ShortcutSuggestionsStorage): void {
  if (!Object.keys(storage).length) {
    localStorage.removeItem(SHORTCUT_SUGGESTIONS_STORAGE_KEY);
    return;
  }

  localStorage.setItem(SHORTCUT_SUGGESTIONS_STORAGE_KEY, JSON.stringify(storage));
}

function cleanupShortcutSuggestionsStorage(
  storage: ShortcutSuggestionsStorage,
): ShortcutSuggestionsStorage {
  const validEntries = Object.entries(storage)
    .filter(([, data]) => isValidShortcutSuggestionData(data))
    .sort(([, prev], [, next]) => next.timestamp - prev.timestamp)
    .slice(0, SHORTCUT_SUGGESTIONS_STORAGE_MAX_PROJECTS);

  return Object.fromEntries(validEntries);
}

/**
 * 保存快捷指令数据
 */
export function saveShortcutSuggestions(data: ShortcutSuggestionData): void {
  try {
    const storage = cleanupShortcutSuggestionsStorage(readShortcutSuggestionsStorage());
    storage[data.projectId] = data;
    writeShortcutSuggestionsStorage(cleanupShortcutSuggestionsStorage(storage));
  } catch (error) {
    console.error('[QuickPrompts] Failed to save shortcut suggestions:', error);
  }
}
export function getShortcutSuggestions(projectId: string): ShortcutSuggestionData | null {
  try {
    const storage = cleanupShortcutSuggestionsStorage(readShortcutSuggestionsStorage());
    const data = storage[projectId];

    if (!data) {
      delete storage[projectId];
      writeShortcutSuggestionsStorage(storage);
      return null;
    }

    storage[projectId] = data;
    writeShortcutSuggestionsStorage(cleanupShortcutSuggestionsStorage(storage));
    return data;
  } catch (error) {
    console.error('[QuickPrompts] Failed to get shortcut instructions:', error);
  }
  return null;
}
export function clearShortcutSuggestions(projectId: string): void {
  try {
    const storage = readShortcutSuggestionsStorage();
    delete storage[projectId];
    writeShortcutSuggestionsStorage(storage);
  } catch (error) {
    console.error('[QuickPrompts] Failed to clear shortcut suggestions:', error);
  }
}
/**
 * 清除所有快捷指令数据
 */
export function clearAllShortcutSuggestions(): void {
  try {
    localStorage.removeItem(SHORTCUT_SUGGESTIONS_STORAGE_KEY);
  } catch {
  }
}

/**
 * 生成快捷指令的请求文本
 */
export function buildShortcutSuggestionRequestText(
  displayName: string,
  content: string,
): string {
  const promptTag = createWegicTag(WEGIC_TAGS.SHORTCUT_SUGGESTION_PROMPT, content);
  return `${displayName}\n${promptTag}`;
}

/**
 * 获取快捷指令匹配到的填充内容（有 id 时调用）
 */
export async function getShortcutSuggestionFillContent(id: string): Promise<string | null> {
  try {
    const { data } = await getShortcutSuggestionDetail(id);
    return data.content || null;
  } catch {
    return null;
  }
}
